import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getAdminUserReadings } from '../../lib/api';
import type { AdminUserListItem, AdminUserReadingsResponse } from '../../lib/types';
import {
  AdminActionButton,
  AdminBadge,
  AdminEmptyState,
  AdminPanel,
  AdminReadingCard,
  AdminStatCard,
  formatDateTime,
} from './AdminUi';

export default function AdminUserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [user, setUser] = useState<AdminUserListItem | null>(null);
  const [response, setResponse] = useState<AdminUserReadingsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    let disposed = false;

    const load = async () => {
      if (!id) {
        if (!disposed) {
          setErrorMessage('사용자 ID가 없습니다.');
          setLoading(false);
        }
        return;
      }

      setLoading(true);
      try {
        const next = await getAdminUserReadings(id);
        if (!disposed) {
          setResponse(next);
          setUser(next.user);
          setErrorMessage(null);
        }
      } catch (error) {
        if (!disposed) {
          const apiError = error as Error;
          setErrorMessage(apiError.message || '사용자 상세 정보를 불러오지 못했습니다.');
        }
      } finally {
        if (!disposed) {
          setLoading(false);
        }
      }
    };

    void load();
    return () => {
      disposed = true;
    };
  }, [id]);

  const pageTitle = user ? user.name || user.email || '사용자 상세' : '사용자 상세';
  const pageSubtitle = user?.email || '최근 리딩과 계정 상태를 확인합니다.';

  return (
    <div className="space-y-6">
      <AdminPanel
        title={pageTitle}
        subtitle={pageSubtitle}
        actions={<AdminActionButton to="/admin/users" label="사용자 목록으로" icon="arrow-right" />}
      >
        {loading ? (
          <p className="text-sm text-[#d8bfa1]/75">사용자 상세를 불러오고 있습니다...</p>
        ) : errorMessage || !user || !response ? (
          <AdminEmptyState
            title="사용자 상세를 불러올 수 없습니다"
            body={errorMessage || '사용자를 찾을 수 없습니다.'}
          />
        ) : (
          <div className="space-y-5">
            <div className="flex flex-wrap gap-2">
              <AdminBadge label={user.provider || 'google'} tone="muted" />
              <AdminBadge label={`활성 세션 ${user.active_session_count}개`} tone="accent" />
            </div>
            <div className="grid gap-4 md:grid-cols-4">
              <AdminStatCard label="리딩 수" value={user.reading_count} />
              <AdminStatCard label="접근 가능" value={user.active_reading_count} />
              <AdminStatCard label="활성 세션" value={user.active_session_count} />
              <AdminStatCard label="최근 리딩" value={formatDateTime(user.latest_reading_at)} />
            </div>
          </div>
        )}
      </AdminPanel>

      <AdminPanel title="최근 리딩" subtitle="이 사용자의 최근 리딩 상태를 확인합니다.">
        {loading ? (
          <p className="text-sm text-[#d8bfa1]/75">리딩 목록을 불러오고 있습니다...</p>
        ) : errorMessage || !response ? (
          <AdminEmptyState
            title="리딩 이력을 불러올 수 없습니다"
            body={errorMessage || '사용자 데이터를 찾을 수 없습니다.'}
          />
        ) : response.readings.length === 0 ? (
          <AdminEmptyState
            title="이 사용자의 리딩이 없습니다"
            body="아직 카드 선택까지 완료한 리딩이 없습니다."
          />
        ) : (
          <div className="space-y-3">
            {response.readings.map((reading) => (
              <AdminReadingCard key={reading.id} reading={reading} to={`/admin/readings/${reading.id}`} />
            ))}
          </div>
        )}
      </AdminPanel>
    </div>
  );
}
